import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import {Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import * as routes from '@app/routes';
import {getProjects} from '@app/stores/projects/projects.selectors';
import {ProjectListAll} from '@app/stores/projects/projects.actions';
import {ProjectContextService} from '@app/services/project-context.service';
import {NavigationMenuService} from '@app/services/navigation-menu.service';

export type ProjectSwitcherVariant = 'navbar' | 'sidebar' | 'rail';

/**
 * The project on screen, and the way to another one. In the top bar it is the navbar's own
 * dropdown (this element IS its <li>, so the navbar's `>` selectors still reach its menu); in
 * the side menu it heads the menu and stays there outside a project too, offering "All
 * projects". Lists the first twenty matches, searching as you type.
 */
@Component({
  selector: '[app-nav-project-switcher]',
  templateUrl: './nav-project-switcher.component.html',
  styleUrls: ['./nav-project-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NavProjectSwitcherComponent implements OnDestroy {
  @Input() variant: ProjectSwitcherVariant = 'navbar';

  readonly context$ = this.projectContext.context$;
  readonly isInfluencer$ = this.menu.isInfluencer$;
  readonly imageDefault = '../assets/images/project-default.png';

  open = false;
  loaded = false;
  search = '';
  projects: any[] = [];

  private readonly search$ = new Subject<string>();
  private readonly destroyed = new Subject<void>();

  @HostBinding('class.show') get isOpen(): boolean {
    return this.open && this.variant === 'navbar';
  }

  constructor(
    private store: Store<any>,
    private router: Router,
    private projectContext: ProjectContextService,
    private menu: NavigationMenuService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {
    this.store.pipe(select(getProjects), takeUntil(this.destroyed)).subscribe((projects) => {
      this.projects = projects || [];
      this.cdr.markForCheck();
    });
    // One request per pause in typing: a request per keystroke let an older, slower response
    // replace the list for the full term.
    this.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed))
      .subscribe((term) => this.load(term));
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open && !this.loaded) this.load('');
  }

  openOnHover(): void {
    if (this.variant !== 'navbar') return;
    if (!this.loaded) this.load('');
  }

  onSearch(term: string): void {
    this.search = term;
    this.search$.next(term);
  }

  choose(projectId: number, isInfluencer: boolean): void {
    this.open = false;
    this.projectContext.switchProject(projectId, isInfluencer);
  }

  allProjects(): void {
    this.open = false;
    this.router.navigate(['/' + routes.projects()]);
  }

  edit(event: Event): void {
    event.stopPropagation();
    this.open = false;
    this.projectContext.editProject();
  }

  trackById(_index: number, project: any): number {
    return project.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.open) return;
    event.stopPropagation();
    this.open = false;
    this.host.nativeElement.querySelector<HTMLElement>('[data-sn-project-toggle]')?.focus();
  }

  private load(search: string): void {
    this.loaded = true;
    this.store.dispatch(ProjectListAll({
      params: {
        options: JSON.stringify({
          fields: ['id', 'title', 'image', 'client_id'],
          orderBy: {column: 'title', dir: 'asc'},
          includePagination: true
        }),
        perPage: 20,
        page: 1,
        search
      }
    }));
  }
}
